/** In-place photo zoom for touch and mouse; browser zoom stays available outside the photo frame. */
export class ProductImageZoom {
  #controller = new AbortController();
  #frame = 0;
  #image;
  #imageQuality = new ZoomImageQuality();
  #scale = 1;
  #x = 0;
  #y = 0;
  #gesture;
  #tap;
  #mouse;
  #outsidePress;
  #wheelExitUntil = 0;
  #uiVisible = false;
  #uiAnimations = [];
  #uiVersion = 0;
  #backdrop;
  #suppressClickUntil = 0;
  #resizeObserver;
  #returning;
  #navigationVersion = 0;
  #historyToken = `photo-zoom-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  #historyEntry = false;
  #historyAttempted = false;
  #historyPending = false;

  constructor(gallery, scroller, controls) {
    this.gallery = gallery;
    this.scroller = scroller;
    this.controls = controls;
    const hint = gallery.querySelector('[data-photo-zoom-hint]');
    // Keep the help row outside the image frame, including its overflow clip.
    if (hint) scroller.closest('slideshow-container')?.after(hint);
    const { signal } = this.#controller;
    scroller.addEventListener('touchstart', this.#start, { passive: false, signal });
    scroller.addEventListener('touchmove', this.#move, { passive: false, signal });
    scroller.addEventListener('touchend', this.#end, { passive: true, signal });
    scroller.addEventListener('touchcancel', this.#cancel, { passive: true, signal });
    scroller.addEventListener('pointerdown', this.#mouseDown, { capture: true, signal });
    window.addEventListener('pointermove', this.#mouseMove, { signal });
    window.addEventListener('pointerup', this.#mouseUp, { signal });
    window.addEventListener('pointercancel', this.#releaseMouse, { signal });
    window.addEventListener('blur', this.#releaseMouse, { signal });
    scroller.addEventListener('lostpointercapture', this.#releaseMouse, { signal });
    scroller.addEventListener('dragstart', event => event.preventDefault(), { signal });
    scroller.addEventListener('wheel', this.#wheel, { passive: false, signal });
    window.addEventListener('keydown', event => {
      if (event.key === 'Escape' && this.#scale > 1) { event.preventDefault(); void this.#resetSmooth(); }
    }, { signal });
    gallery.addEventListener('click', this.#click, { capture: true, signal });
    // Capture before Horizon's document-level link/cart handlers. An outside
    // dismissal must not also activate the control underneath the dimming.
    window.addEventListener('pointerdown', this.#outsideDown, { capture: true, signal });
    window.addEventListener('pointermove', this.#outsideMove, { capture: true, signal });
    window.addEventListener('pointercancel', () => { this.#outsidePress = undefined; }, { capture: true, signal });
    window.addEventListener('click', this.#outsideClick, { capture: true, signal });
    controls.querySelector('button').addEventListener('click', this.#resetSmooth, { signal });
    window.visualViewport?.addEventListener('resize', this.#viewportChanged, { signal });
    window.addEventListener('popstate', this.#popState, { signal });
    document.addEventListener('scroll', this.#updateBackdrop, { capture: true, passive: true, signal });
    document.addEventListener('transitionend', this.#updateBackdrop, { capture: true, signal });
    document.addEventListener('touchmove', this.#preventPageScroll, { capture: true, passive: false, signal });
    document.addEventListener('wheel', this.#preventPageScroll, { capture: true, passive: false, signal });
    window.addEventListener('resize', this.#updateBackdrop, { passive: true, signal });
    this.#resizeObserver = new ResizeObserver(() => {
      if (this.#scale > 1) {
        // Keep the dimming cutouts aligned during the mobile help-row reveal,
        // in this layout pass rather than one frame behind the moving rail.
        if (this.#uiAnimations.length) this.#updateBackdrop();
        this.#schedule();
      }
    });
    this.#resizeObserver.observe(scroller);
    this.#resizeObserver.observe(gallery);
    this.#viewportChanged();
    // Reveal only after the photo's interaction handlers have been installed.
    scroller.setAttribute('data-photo-pinch-ready', '');
    document.dispatchEvent(new Event('product-photo-zoom-ready'));
  }

  #viewportChanged = () => {
    // Let a magnified browser viewport pan in either direction, not the carousel.
    this.scroller.toggleAttribute('data-page-zoomed', (window.visualViewport?.scale ?? 1) > 1.01);
  };

  #isPhotoControl(target) {
    return this.scroller.contains(target) || (this.gallery.contains(target) && Boolean(target.closest(
      'slideshow-controls, slideshow-arrows, .slideshow-controls__arrows, [data-photo-zoom-controls], [data-photo-zoom-trigger], .product-gallery-lightbox-trigger'
    )));
  }

  #outsideDown = (event) => {
    this.#outsidePress = undefined;
    if (!this.gallery.hasAttribute('data-photo-zoom-active') || !event.isPrimary || event.button !== 0 ||
        !(event.target instanceof Element) || this.#isPhotoControl(event.target)) return;
    this.#outsidePress = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
  };

  #outsideMove = (event) => {
    const press = this.#outsidePress;
    if (press?.id === event.pointerId && Math.hypot(event.clientX - press.x, event.clientY - press.y) > 10) press.moved = true;
  };

  #outsideClick = (event) => {
    const press = this.#outsidePress;
    this.#outsidePress = undefined;
    if (!this.gallery.hasAttribute('data-photo-zoom-active') || event.detail === 0 ||
        !(event.target instanceof Element) || this.#isPhotoControl(event.target)) return;
    // Ignore releases from a photo pan and outside scroll/drag gestures. Only
    // a new stationary press on the shaded area dismisses the zoom.
    if (!press || press.moved || Math.hypot(event.clientX - press.x, event.clientY - press.y) > 10) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void this.#resetSmooth(event);
  };

  #preventPageScroll = (event) => {
    // Consume the tail of the wheel gesture that just returned the photo to
    // 100%. Release after a short pause, not midway through trackpad inertia.
    if (event.type === 'wheel' && !event.ctrlKey && performance.now() < this.#wheelExitUntil) {
      this.#wheelExitUntil = performance.now() + 450;
      if (event.cancelable) event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (!this.gallery.hasAttribute('data-photo-zoom-active') || event.ctrlKey) return;
    // The thumbnail rail retains native scrolling. CSS contains its overscroll
    // so reaching either end cannot pass the gesture through to the page.
    if (event.target instanceof Element && this.gallery.contains(event.target) &&
        event.target.closest('slideshow-controls') && (!event.touches || event.touches.length === 1)) return;
    if (event.cancelable) event.preventDefault();
  };

  #start = (event) => {
    this.#tap = undefined;
    if (this.#historyPending || this.#returning) return;
    if ((window.visualViewport?.scale ?? 1) > 1.01) return;
    if (!(event.target instanceof Element) || event.target.closest('button, a, video, deferred-media, product-model')) return;
    const slide = event.target.closest('slideshow-slide.product-media-container--image');
    if (!slide || slide.getAttribute('aria-hidden') === 'true') return;
    const image = slide.querySelector('img.product-media__image');
    if (!image) return;
    if (event.touches.length === 1 && this.#scale === 1) {
      const touch = event.touches[0];
      this.#tap = { image, x: touch.clientX, y: touch.clientY, time: performance.now() };
      return;
    }
    image.removeAttribute('data-photo-zoom-ease');
    if (this.#image && this.#image !== image) this.reset();
    this.#image = image;
    // Stop the native pinch before it starts, without suppressing normal page scroll.
    if (event.cancelable) event.preventDefault();
    this.gallery.setAttribute('data-photo-zoom-active', '');
    this.#begin(event.touches);
  };

  #begin(touches) {
    const first = touches[0];
    if (!first) { this.#gesture = undefined; return; }
    const second = touches[1];
    const rect = this.scroller.getBoundingClientRect();
    this.#gesture = {
      count: second ? 2 : 1,
      distance: second ? Math.max(1, Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY)) : 1,
      midX: (second ? (first.clientX + second.clientX) / 2 : first.clientX) - rect.left - rect.width / 2,
      midY: (second ? (first.clientY + second.clientY) / 2 : first.clientY) - rect.top - rect.height / 2,
      scale: this.#scale, x: this.#x, y: this.#y,
      left: rect.left, top: rect.top, width: rect.width, height: rect.height,
    };
  }

  #move = (event) => {
    if (this.#tap) {
      const touch = event.touches[0];
      if (event.touches.length !== 1 || !touch || Math.hypot(touch.clientX - this.#tap.x, touch.clientY - this.#tap.y) > 10) {
        this.#tap = undefined;
      }
    }
    const gesture = this.#gesture;
    if (!gesture || !this.#image) return;
    if (event.cancelable) event.preventDefault();
    this.#suppressClickUntil = performance.now() + 500;
    const first = event.touches[0];
    const second = event.touches[1];
    if (!first) return;
    if ((second ? 2 : 1) !== gesture.count) { this.#begin(event.touches); return; }
    const midX = (second ? (first.clientX + second.clientX) / 2 : first.clientX) - gesture.left - gesture.width / 2;
    const midY = (second ? (first.clientY + second.clientY) / 2 : first.clientY) - gesture.top - gesture.height / 2;
    if (second) {
      const distance = Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY);
      this.#scale = Math.min(5, Math.max(1, gesture.scale * distance / gesture.distance));
    }
    const ratio = this.#scale / gesture.scale;
    this.#x = midX - (gesture.midX - gesture.x) * ratio;
    this.#y = midY - (gesture.midY - gesture.y) * ratio;
    this.#schedule();
  };

  #end = (event) => {
    const tap = this.#tap;
    this.#tap = undefined;
    if (tap && !event.touches.length && performance.now() - tap.time < 500) {
      this.#zoomAt(tap.image, tap.x, tap.y);
      return;
    }
    if (!this.#gesture) return;
    this.#suppressClickUntil = performance.now() + 500;
    // Rebase when one finger lifts; the remaining finger can pan without jumping.
    this.#render();
    this.#begin(event.touches);
    if (!event.touches.length && this.#scale <= 1.01) this.reset();
  };

  #cancel = () => {
    this.#tap = undefined;
    this.#gesture = undefined;
    this.#suppressClickUntil = performance.now() + 500;
    if (this.#scale <= 1.01) this.reset();
  };

  #mouseDown = (event) => {
    if (event.pointerType === 'touch' || event.button !== 0 || !event.isPrimary ||
        event.ctrlKey || event.metaKey || event.altKey || event.shiftKey || this.#returning || this.#historyPending ||
        (window.visualViewport?.scale ?? 1) > 1.01) return;
    if (!(event.target instanceof Element) || event.target.closest('button, a, video, deferred-media, product-model')) return;
    const slide = event.target.closest('slideshow-slide.product-media-container--image');
    if (!slide || slide.getAttribute('aria-hidden') === 'true') return;
    const image = slide.querySelector('img.product-media__image');
    if (!image) return;
    this.#mouse = { id: event.pointerId, image, startX: event.clientX, startY: event.clientY,
      x: this.#x, y: this.#y, moved: false, panning: this.#scale > 1 };
    if (this.#mouse.panning) {
      image.removeAttribute('data-photo-zoom-ease');
      this.scroller.setPointerCapture(event.pointerId);
      this.gallery.setAttribute('data-photo-zoom-panning', '');
      event.preventDefault();
    }
  };

  #mouseMove = (event) => {
    const mouse = this.#mouse;
    if (!mouse || event.pointerId !== mouse.id) return;
    const dx = event.clientX - mouse.startX, dy = event.clientY - mouse.startY;
    if (Math.hypot(dx, dy) > 8) mouse.moved = true;
    if (!mouse.panning) return; // An unzoomed drag still belongs to the slideshow.
    this.#x = mouse.x + dx; this.#y = mouse.y + dy;
    this.#schedule();
  };

  #releaseMouse = () => {
    const mouse = this.#mouse;
    this.#mouse = undefined;
    this.gallery.removeAttribute('data-photo-zoom-panning');
    if (mouse && this.scroller.hasPointerCapture(mouse.id)) this.scroller.releasePointerCapture(mouse.id);
  };

  #mouseUp = (event) => {
    const mouse = this.#mouse;
    if (!mouse || mouse.id !== event.pointerId) return;
    this.#releaseMouse();
    if (mouse.panning || mouse.moved) this.#suppressClickUntil = performance.now() + 500;
    else this.#zoomAt(mouse.image, event.clientX, event.clientY);
  };

  #wheel = (event) => {
    if (this.#returning || this.#historyPending || (window.visualViewport?.scale ?? 1) > 1.01) return;
    // Ordinary scrolling at 100% still scrolls the page. A trackpad pinch over
    // the photo (ctrl+wheel) or a wheel while zoomed adjusts only this image.
    if (this.#scale === 1 && !event.ctrlKey) return;
    const image = this.scroller.querySelector('slideshow-slide.product-media-container--image[aria-hidden="false"] img.product-media__image');
    if (!image) return;
    event.preventDefault();
    const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.scroller.clientHeight : 1);
    // Smaller wheel steps for precise inspection; preserve trackpad pinch feel.
    const sensitivity = event.ctrlKey ? 0.002 : 0.0008;
    this.#setScaleAt(image, event.clientX, event.clientY, this.#scale * Math.exp(-delta * sensitivity), false);
    if (this.#scale <= 1.01) {
      this.reset();
      this.#wheelExitUntil = performance.now() + 450;
    }
  };

  zoomCurrent = () => {
    if (this.#returning || this.#historyPending) return;
    const image = this.scroller.querySelector('slideshow-slide.product-media-container--image[aria-hidden="false"] img.product-media__image');
    if (!image) return;
    const rect = this.scroller.getBoundingClientRect();
    this.#setScaleAt(image, rect.left + rect.width / 2, rect.top + rect.height / 2, this.#scale + 0.5, true);
  };

  #click = (event) => {
    if (event.target instanceof Element && event.target.closest('[data-photo-zoom-controls]')) return;
    if (event.target instanceof Element && event.target.closest('[data-photo-zoom-trigger]')) {
      event.preventDefault(); event.stopImmediatePropagation();
      this.zoomCurrent();
      return;
    }
    // Explicit navigation is distinct from dragging the magnified photo.
    if (event.target instanceof Element && event.target.closest('slideshow-controls, slideshow-arrows, .slideshow-controls__arrows, .product-gallery-lightbox-trigger')) {
      // Slideshow.select owns the awaited zoom-out before changing media. Its
      // document-capture handler runs before this listener; don't cancel it.
      if (event.target.closest('.product-gallery-lightbox-trigger')) this.reset();
      return;
    }
    if (!this.gallery.hasAttribute('data-photo-zoom-active') && performance.now() >= this.#suppressClickUntil) return;
    // The synthesized click after a tap/pinch/pan must not open the lightbox.
    if (event.target instanceof Element && event.target.closest('slideshow-component')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  #zoomAt(image, x, y) {
    this.#setScaleAt(image, x, y, 1.5, true);
  }

  #setScaleAt(image, x, y, scale, ease) {
    if (this.#historyPending) return;
    this.#wheelExitUntil = 0;
    const rect = this.scroller.getBoundingClientRect();
    this.#image = image;
    const next = Math.min(5, Math.max(1, scale)), ratio = next / this.#scale;
    const anchorX = x - rect.left - rect.width / 2, anchorY = y - rect.top - rect.height / 2;
    this.#x = anchorX - (anchorX - this.#x) * ratio;
    this.#y = anchorY - (anchorY - this.#y) * ratio;
    this.#scale = next;
    this.#suppressClickUntil = performance.now() + 500;
    image.toggleAttribute('data-photo-zoom-ease', ease);
    this.gallery.setAttribute('data-photo-zoom-active', '');
    this.#render();
  }

  #setZoomUI(visible, immediate = false) {
    if (visible === this.#uiVisible && !immediate) return;
    this.#uiVisible = visible;
    const version = ++this.#uiVersion;
    const elements = [this.controls, this.gallery.querySelector('[data-photo-zoom-hint]')].filter(Boolean);
    const animate = !immediate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
      elements.every(element => typeof element.animate === 'function');
    const slideHint = animate && window.matchMedia('(max-width: 749px)').matches;
    const from = animate ? elements.map(element => element.hidden ? 0 : Number(getComputedStyle(element).opacity)) : [];
    const hint = elements.find(element => element.hasAttribute('data-photo-zoom-hint'));
    const readHintLayout = () => {
      if (!hint || hint.hidden) return { height: '0px', paddingTop: '0px', paddingBottom: '0px', marginTop: '0px' };
      const style = getComputedStyle(hint);
      return { height: `${hint.getBoundingClientRect().height}px`, paddingTop: style.paddingTop,
        paddingBottom: style.paddingBottom, marginTop: style.marginTop };
    };
    const hintFrom = slideHint ? readHintLayout() : undefined;
    this.#uiAnimations.forEach(animation => animation.cancel());
    this.#uiAnimations = [];
    elements.forEach((element, index) => {
      if (!animate) { element.hidden = !visible; element.inert = !visible; return; }
      // Reset must remain usable during a thumbnail's return animation so it
      // can still cancel that pending selection, as it did before fading.
      element.inert = false;
      element.hidden = false;
      const start = { opacity: from[index] }, end = { opacity: visible ? 1 : 0 };
      if (slideHint && element === hint) {
        // Grow actual space in the mobile flow so thumbnails and subsequent
        // content move together. Clipping keeps the help out of their way.
        Object.assign(start, hintFrom, { minHeight: '0px', overflow: 'clip' });
        Object.assign(end, visible ? readHintLayout() : { height: '0px', paddingTop: '0px', paddingBottom: '0px', marginTop: '0px' },
          { minHeight: '0px', overflow: 'clip' });
      }
      const animation = element.animate([start, end], {
        duration: visible ? (slideHint ? 280 : 180) : 320, easing: 'ease-out', fill: 'both',
      });
      this.#uiAnimations.push(animation);
    });
    if (!animate) return;
    // Hints and Reset share a lifetime and timing, including rapid re-entry.
    const animations = this.#uiAnimations;
    void Promise.all(animations.map(animation => animation.finished)).then(() => {
      if (version !== this.#uiVersion) return;
      elements.forEach(element => { element.hidden = !visible; element.inert = !visible; });
      animations.forEach(animation => animation.cancel());
      this.#uiAnimations = [];
    }).catch(() => { /* Superseded by reset, re-entry, or section teardown. */ });
  }

  #updateBackdrop = () => {
    if (!this.#backdrop) return;
    // The rail shades its own background in CSS. Do not track moving thumbnail
    // buttons with fixed holes: compositor scrolling can outrun those updates.
    const elements = [this.scroller, ...this.gallery.querySelectorAll('slideshow-controls, .slideshow-controls__arrows button, [data-photo-zoom-hint]')];
    // A sticky menu may overlap the photo after scrolling. It must not inherit
    // the photo's transparent cutout just because their rectangles intersect.
    const menus = [...document.querySelectorAll('#header-group > *, #header-component')]
      .map((element) => element.getBoundingClientRect())
      .filter((rect) => rect.width && rect.height);
    const holes = [];
    for (const element of elements) {
      const rect = element.getBoundingClientRect();
      if (!rect.width || !rect.height) continue;
      let pieces = [{ left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom }];
      if (element.localName === 'slideshow-controls') {
        const ratio = window.devicePixelRatio || 1;
        // Element backgrounds round on the CSS pixel grid. At fractional
        // display scaling, use edges shared by that grid
        // and physical pixels so two shade layers cannot leave a hairline.
        let step = 1;
        while (step < 16 && Math.abs(step * ratio - Math.round(step * ratio)) > 0.001) step++;
        if (step === 16) step = 1;
        const snap = value => Math.round(value / step) * step;
        const left = snap(rect.left), top = snap(rect.top);
        const right = snap(rect.right), bottom = snap(rect.bottom);
        const width = right - left, height = bottom - top;
        element.style.setProperty('--photo-zoom-shade-inset', `${top - rect.top}px auto auto ${left - rect.left}px`);
        element.style.setProperty('--photo-zoom-shade-width', `${width}px`);
        element.style.setProperty('--photo-zoom-shade-height', `${height}px`);
        pieces = [{ left, top, right, bottom }];
      }
      for (const menu of menus) pieces = pieces.flatMap((piece) => subtractRect(piece, menu));
      // Non-overlapping holes avoid even-odd clipping darkening intersections.
      for (const covered of holes) pieces = pieces.flatMap((piece) => subtractRect(piece, covered));
      const radius = element.hasAttribute('data-photo-zoom-hint')
        ? Number.parseFloat(getComputedStyle(element).borderTopLeftRadius) || 0 : 0;
      // The hint is rounded, so its clear window must not expose the page
      // behind the four corners of its rectangular bounding box.
      const rounded = radius > 0 ? { left: rect.left, top: rect.top, width: rect.width, height: rect.height,
        radius: Math.min(radius, rect.width / 2, rect.height / 2) } : undefined;
      holes.push(...pieces.map(piece => rounded ? { ...piece, rounded } : piece));
    }
    if (window.CSS?.supports('mask-composite', 'exclude')) {
      // Rectangular mask tiles rasterize like the rail's rectangular shade;
      // a polygon clip antialiases these shared edges differently at zoom.
      this.#backdrop.style.maskImage = ['linear-gradient(#000 0 0)', ...holes.map(rect => {
        if (!rect.rounded) return 'linear-gradient(#000 0 0)';
        const shape = rect.rounded;
        // Each tile retains the original curve even when a sticky header or
        // another clear window has split the hint into smaller visible pieces.
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${rect.left - shape.left} ${rect.top - shape.top} ${rect.right - rect.left} ${rect.bottom - rect.top}"><rect width="${shape.width}" height="${shape.height}" rx="${shape.radius}" fill="black"/></svg>`;
        return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
      })].join(', ');
      this.#backdrop.style.maskSize = ['100% 100%', ...holes.map(rect => `${rect.right - rect.left}px ${rect.bottom - rect.top}px`)].join(', ');
      this.#backdrop.style.maskPosition = ['0 0', ...holes.map(rect => `${rect.left}px ${rect.top}px`)].join(', ');
      this.#backdrop.style.maskRepeat = 'no-repeat';
      this.#backdrop.style.maskComposite = 'exclude';
    } else {
      const points = ['0 0', '100% 0', '100% 100%', '0 100%', '0 0'];
      for (const rect of holes) {
        let outline = [[rect.left, rect.top], [rect.right, rect.top], [rect.right, rect.bottom], [rect.left, rect.bottom]];
        if (rect.rounded) {
          const { left, top, width, height, radius } = rect.rounded;
          const centers = [[left + width - radius, top + radius], [left + width - radius, top + height - radius],
            [left + radius, top + height - radius], [left + radius, top + radius]];
          outline = centers.flatMap(([x, y], corner) => Array.from({ length: 9 }, (_, step) => {
            const angle = ((corner - 1) * 90 + step * 90 / 8) * Math.PI / 180;
            return [Math.max(rect.left, Math.min(rect.right, x + Math.cos(angle) * radius)),
              Math.max(rect.top, Math.min(rect.bottom, y + Math.sin(angle) * radius))];
          }));
        }
        const polygon = outline.map(([x, y]) => `${x}px ${y}px`);
        points.push(...polygon, polygon[0], '0 0');
      }
      this.#backdrop.style.clipPath = `polygon(evenodd, ${points.join(', ')})`;
    }
  };

  #schedule() {
    if (!this.#frame) this.#frame = requestAnimationFrame(this.#render);
  }

  #registerHistory() {
    if (this.#historyAttempted || this.#scale <= 1.01) return;
    this.#historyAttempted = true;
    const state = window.history.state;
    // Do not replace a non-object state owned by another application.
    if (state !== null && (typeof state !== 'object' || Array.isArray(state))) return;
    try {
      window.history.pushState({ ...state, chemaPhotoZoom: this.#historyToken }, '');
      this.#historyEntry = true;
    } catch {
      // A restricted preview may reject history writes; zoom/reset still work.
    }
  }

  #popState = () => {
    this.#historyPending = false;
    if (window.history.state?.chemaPhotoZoom === this.#historyToken) {
      // Forward must not restore an invisible zoom lock or revive a stale entry.
      if (!this.#historyEntry) {
        const { chemaPhotoZoom, ...state } = window.history.state;
        try { window.history.replaceState(state, ''); } catch { /* Restricted preview. */ }
      }
      return;
    }
    if (!this.#historyEntry) return;
    this.#historyEntry = false;
    // Back already consumed the zoom entry. Animate without traversing again.
    this.#resetSmooth();
  };

  #releaseHistory() {
    if (!this.#historyEntry) return;
    this.#historyEntry = false;
    // Never traverse an entry that a variant change or other UI has replaced.
    if (window.history.state?.chemaPhotoZoom !== this.#historyToken) return;
    this.#historyPending = true;
    try { window.history.back(); } catch { this.#historyPending = false; }
  }

  #render = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    if (!this.#image) return;
    if (!this.#returning && this.#scale > 1.01) void this.#imageQuality.upgrade(this.#image, { gallery: this.scroller });
    this.#registerHistory();
    if (!this.#returning && this.#scale > 1.01) this.#setZoomUI(true);
    if (this.#scale > 1.01 && !this.#backdrop) {
      this.#backdrop = document.createElement('div');
      this.#backdrop.className = 'product-photo-zoom-backdrop';
      this.#backdrop.setAttribute('aria-hidden', 'true');
      document.body.append(this.#backdrop);
    }
    if (this.#backdrop) {
      // Light gray near 100%, then progressively darker to opaque black at 500%.
      const progress = Math.max(0, Math.min(1, (this.#scale - 1) / 4));
      const opacity = (0.04 + 0.96 * progress).toFixed(3);
      this.#backdrop.style.setProperty('--photo-zoom-backdrop-opacity', opacity);
      this.gallery.style.setProperty('--photo-zoom-backdrop-opacity', opacity);
    }
    this.#updateBackdrop();
    // The photo fills the frame with the gallery's existing centered crop.
    const maxX = Math.max(0, (this.#image.clientWidth * this.#scale - this.scroller.clientWidth) / 2);
    const maxY = Math.max(0, (this.#image.clientHeight * this.#scale - this.scroller.clientHeight) / 2);
    this.#x = Math.max(-maxX, Math.min(maxX, this.#x));
    this.#y = Math.max(-maxY, Math.min(maxY, this.#y));
    for (const image of [this.#image, this.#image.parentElement?.querySelector('.product-media__preview')]) {
      if (!image) continue;
      image.style.setProperty('--photo-zoom-scale', String(this.#scale));
      image.style.setProperty('--photo-zoom-x', `${this.#x}px`);
      image.style.setProperty('--photo-zoom-y', `${this.#y}px`);
      image.setAttribute('data-photo-zoom-image', '');
    }
    this.controls.querySelector('output').textContent = `${Math.round(this.#scale * 100)}%`;
  };

  /** Return to the full photo before Slideshow starts its existing crossfade.
   * Also shared by Reset/Back; the latest intent cancels any pending selection. */
  async prepareNavigation() {
    const version = ++this.#navigationVersion;
    if (!this.#returning) {
      if (!this.#image || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.reset();
        return true;
      }
      cancelAnimationFrame(this.#frame);
      this.#frame = 0;
      this.#gesture = this.#tap = undefined;
      this.#releaseMouse();
      // A quick thumbnail tap can interrupt the initial tap-to-zoom easing.
      // Continue from the rendered transform/shade, not their unfinished target.
      const transform = new DOMMatrixReadOnly(getComputedStyle(this.#image).transform);
      const scale = transform.a, x = transform.m41, y = transform.m42;
      const opacity = this.#backdrop ? Number(getComputedStyle(this.#backdrop).opacity) : 0;
      this.#image.removeAttribute('data-photo-zoom-ease');
      this.gallery.setAttribute('data-photo-zoom-returning', '');
      this.#backdrop?.setAttribute('data-photo-zoom-returning', '');
      this.#setZoomUI(false);
      const start = performance.now();
      let resolve;
      const promise = new Promise(done => { resolve = done; });
      const returning = { promise, resolve, frame: 0 };
      this.#returning = returning;
      const step = (now) => {
        if (this.#returning !== returning) return;
        const progress = Math.min(1, (now - start) / 320);
        const remaining = (1 - progress) ** 3;
        this.#scale = 1 + (scale - 1) * remaining;
        this.#x = x * remaining;
        this.#y = y * remaining;
        this.#render();
        // Fade all the way to clear, including the thumbnail rail, before reset.
        const shade = String(opacity * remaining);
        this.gallery.style.setProperty('--photo-zoom-backdrop-opacity', shade);
        this.#backdrop?.style.setProperty('--photo-zoom-backdrop-opacity', shade);
        if (progress < 1) returning.frame = requestAnimationFrame(step);
        else {
          this.#returning = undefined;
          this.reset();
          resolve(true);
        }
      };
      returning.frame = requestAnimationFrame(step);
    }
    const completed = await this.#returning.promise;
    return completed && version === this.#navigationVersion && !this.#controller.signal.aborted;
  }

  #resetSmooth = async (event) => {
    // Reuse an in-progress return without restarting it. A Reset/Back press
    // supersedes thumbnail navigation but leaves the currently visible photo.
    const completed = await this.prepareNavigation();
    if (completed && event && !this.#controller.signal.aborted) this.scroller.focus({ preventScroll: true });
  };

  // Immediate cleanup is still necessary for teardown and external media updates.
  reset = (event) => {
    this.#imageQuality.pause();
    this.#wheelExitUntil = 0;
    if (this.#returning) {
      cancelAnimationFrame(this.#returning.frame);
      this.#returning.resolve(false);
      this.#returning = undefined;
    }
    this.gallery.removeAttribute('data-photo-zoom-returning');
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#gesture = undefined;
    this.#tap = undefined;
    this.#releaseMouse();
    this.#outsidePress = undefined;
    this.#backdrop?.remove();
    this.#backdrop = undefined;
    this.#setZoomUI(false);
    this.#scale = 1;
    this.#x = this.#y = 0;
    for (const image of [this.#image, this.#image?.parentElement?.querySelector('.product-media__preview')]) {
      if (!image) continue;
      image.removeAttribute('data-photo-zoom-image');
      image.removeAttribute('data-photo-zoom-ease');
      for (const property of ['--photo-zoom-scale', '--photo-zoom-x', '--photo-zoom-y']) image.style.removeProperty(property);
    }
    this.#image = undefined;
    this.gallery.removeAttribute('data-photo-zoom-active');
    this.gallery.style.removeProperty('--photo-zoom-backdrop-opacity');
    for (const rail of this.gallery.querySelectorAll('slideshow-controls')) {
      rail.style.removeProperty('--photo-zoom-shade-inset');
      rail.style.removeProperty('--photo-zoom-shade-width');
      rail.style.removeProperty('--photo-zoom-shade-height');
    }
    this.controls.querySelector('output').textContent = '100%';
    this.#releaseHistory();
    this.#historyAttempted = false;
    if (event) this.scroller.focus({ preventScroll: true });
  };

  destroy() {
    this.reset();
    this.#imageQuality.destroy();
    this.#setZoomUI(false, true);
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.scroller.removeAttribute('data-photo-pinch-ready');
    this.scroller.removeAttribute('data-page-zoomed');
  }
}

/** Fetch the source encoding only on zoom; never blank the visible image while
 * downloading/decoding it. In particular, PNG uploads must not be negotiated to
 * lossy WebP/AVIF. Bound originals to the active photo and a small recent set. */
export class ZoomImageQuality {
  #entries = new Map();
  #destroyed = false;
  #background;
  #activeImage;
  #lifecycle = new AbortController();

  constructor() {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.#cancelBackground();
    }, { signal: this.#lifecycle.signal });
    navigator.connection?.addEventListener?.('change', () => {
      const connection = navigator.connection;
      if (connection.saveData || ['slow-2g', '2g', '3g'].includes(connection.effectiveType)) this.#cancelBackground();
    }, { signal: this.#lifecycle.signal });
  }

  pause() {
    this.#cancelBackground();
    const active = this.#entries.get(this.#activeImage);
    if (active) active.aheadScheduled = false;
    this.#activeImage = undefined;
  }

  #cancelBackground() {
    const background = this.#background;
    this.#background = undefined;
    if (background && !background.entry.demanded && this.#entries.get(background.image) === background.entry) {
      this.#release(background.image, background.entry);
      this.#entries.delete(background.image);
    }
  }

  #prune() {
    // The current image must never downgrade mid-gesture. Inactive images can
    // return to their responsive display source before their blob is revoked.
    let pixels = [...this.#entries.values()].reduce((sum, entry) => sum + (entry.pixels || 0), 0);
    for (const [image, entry] of this.#entries) {
      // Soft decoded-pixel budget; one explicitly requested original may exceed
      // it. Never reduce that active original's fidelity to meet a cache limit.
      if (this.#entries.size <= 3 && pixels <= 24000000) break;
      if (image === this.#activeImage || image.hasAttribute('data-photo-zoom-image')) continue;
      pixels -= entry.pixels || 0;
      this.#release(image, entry);
      this.#entries.delete(image);
    }
  }

  upgrade(image, { gallery, priority = 'high' } = {}) {
    if (this.#destroyed) return;
    const source = image.getAttribute('data_max_resolution');
    if (!source) return;
    let url;
    try { url = new URL(source, document.baseURI); } catch { return; }
    if (!['http:', 'https:'].includes(url.protocol)) return;
    const extension = url.pathname.split('.').pop()?.toLowerCase();
    const accept = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
      webp: 'image/webp', avif: 'image/avif', gif: 'image/gif' }[extension];
    if (!accept) return;
    if (priority === 'high' && this.#activeImage !== image) {
      this.#activeImage = image;
      if (this.#background?.image !== image) this.#cancelBackground();
    }
    let existing = this.#entries.get(image);
    if (existing?.failedAt && Date.now() - existing.failedAt > 30000) {
      this.#release(image, existing);
      this.#entries.delete(image);
      existing = undefined;
    }
    if (existing?.source === source) {
      if (priority === 'high') existing.demanded = true;
      this.#entries.delete(image);
      this.#entries.set(image, existing);
      if (gallery) this.#warmNext(image, existing, gallery);
      return existing.promise;
    }
    if (existing) this.#release(image, existing);
    const entry = {
      source, src: image.getAttribute('src'), srcset: image.getAttribute('srcset'),
      controller: new AbortController(), objectUrl: null, promise: null,
      demanded: priority === 'high', aheadScheduled: false,
    };
    this.#entries.set(image, entry);
    this.#prune();
    entry.promise = this.#load(image, entry, url.href, accept, priority);
    if (gallery) this.#warmNext(image, entry, gallery);
    return entry.promise;
  }

  #warmNext(image, entry, gallery) {
    if (entry.aheadScheduled) return;
    entry.aheadScheduled = true;
    void entry.promise.then(() => {
      if (this.#destroyed || document.hidden || this.#activeImage !== image || !entry.objectUrl || this.#entries.get(image) !== entry || !image.isConnected ||
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'true') return;
      const connection = globalThis.navigator?.connection;
      if (connection?.saveData || ['slow-2g', '2g', '3g'].includes(connection?.effectiveType)) return;
      const photos = [...gallery.querySelectorAll('slideshow-slide.product-media-container--image:not([hidden]) img.product-media__image')];
      const index = photos.indexOf(image);
      if (index < 0 || photos.length < 2) return;
      const next = photos[(index + 1) % photos.length];
      const nextPixels = Number(next.getAttribute('width')) * Number(next.getAttribute('height'));
      if ((entry.pixels || 0) + nextPixels > 24000000) return;
      // Bound speculative traffic to one photo. A request the user has since
      // zoomed into is no longer speculative and must not be cancelled.
      if (this.#background && this.#background.image !== next && !this.#background.entry.demanded) {
        const previous = this.#background;
        if (this.#entries.get(previous.image) === previous.entry) {
          this.#release(previous.image, previous.entry);
          this.#entries.delete(previous.image);
        }
      }
      const promise = this.upgrade(next, { priority: 'low' });
      const nextEntry = this.#entries.get(next);
      if (!promise || !nextEntry) return;
      const background = { image: next, entry: nextEntry };
      this.#background = background;
      // No recursive prefetch: photo N only warms N+1, never the whole gallery.
      void promise.finally(() => {
        if (this.#background === background) this.#background = undefined;
      });
    });
  }

  async #load(image, entry, url, accept, priority) {
    const { signal } = entry.controller;
    const timeout = setTimeout(() => entry.controller.abort(), 20000);
    try {
      // Don't reuse an optimized <img> response, or put the heavier source in
      // its normal browsing cache. The blob itself is reused on later zooms.
      const response = await fetch(url, { headers: { Accept: accept }, credentials: 'omit', cache: 'no-store', priority, signal });
      if (!response.ok || response.headers.get('content-type')?.split(';')[0].trim() !== accept) return;
      const blob = await response.blob();
      if (signal.aborted || this.#destroyed) return;
      entry.objectUrl = URL.createObjectURL(blob);
      const decoded = new Image();
      decoded.decoding = 'async';
      if (typeof decoded.decode === 'function') {
        decoded.src = entry.objectUrl;
        await decoded.decode();
      } else {
        await new Promise((resolve, reject) => {
          decoded.onload = resolve;
          decoded.onerror = reject;
          decoded.src = entry.objectUrl;
        });
      }
      // A section/variant update must not receive a stale photo. Switching to
      // another slide is safe: only this original image element is upgraded.
      if (signal.aborted || this.#destroyed || this.#entries.get(image) !== entry || !image.isConnected ||
          image.getAttribute('data_max_resolution') !== entry.source || image.getAttribute('src') !== entry.src ||
          image.getAttribute('srcset') !== entry.srcset || !decoded.naturalWidth ||
          decoded.naturalWidth < image.naturalWidth || decoded.naturalHeight < image.naturalHeight) {
        if (entry.objectUrl) URL.revokeObjectURL(entry.objectUrl);
        entry.objectUrl = null;
        return;
      }
      image.removeAttribute('srcset');
      image.src = entry.objectUrl;
      entry.pixels = decoded.naturalWidth * decoded.naturalHeight;
    } catch {
      // Offline, unsupported negotiation, decode errors, and teardown all keep
      // the existing image usable. Don't retry on every pinch animation frame.
      if (entry.objectUrl) URL.revokeObjectURL(entry.objectUrl);
      entry.objectUrl = null;
    } finally {
      clearTimeout(timeout);
      if (!entry.objectUrl) entry.failedAt = Date.now();
      this.#prune();
    }
  }

  #release(image, entry) {
    entry.controller.abort();
    if (!entry.objectUrl) return;
    if (image.getAttribute('src') === entry.objectUrl) {
      if (entry.srcset !== null) image.setAttribute('srcset', entry.srcset);
      if (entry.src !== null) image.setAttribute('src', entry.src);
      else image.removeAttribute('src');
    }
    URL.revokeObjectURL(entry.objectUrl);
    entry.objectUrl = null;
  }

  destroy() {
    this.#destroyed = true;
    this.#lifecycle.abort();
    for (const [image, entry] of this.#entries) this.#release(image, entry);
    this.#entries.clear();
    this.#background = undefined;
  }
}

/** Subtract one rectangular hole from another before building an even-odd mask. */
export function subtractRect(rect, covered) {
  const left = Math.max(rect.left, covered.left), right = Math.min(rect.right, covered.right);
  const top = Math.max(rect.top, covered.top), bottom = Math.min(rect.bottom, covered.bottom);
  if (left >= right || top >= bottom) return [rect];
  return [
    { left: rect.left, right: rect.right, top: rect.top, bottom: top },
    { left: rect.left, right: rect.right, top: bottom, bottom: rect.bottom },
    { left: rect.left, right: left, top, bottom },
    { left: right, right: rect.right, top, bottom },
  ].filter((piece) => piece.left < piece.right && piece.top < piece.bottom);
}

// Independent of Horizon's gallery/vendor dependency graph: zoom upgrades first.
export class ProductPhotoZoom extends HTMLElement {
  controller;
  #startupObserver;

  #initialize = () => {
    if (!this.isConnected || this.controller) return Boolean(this.controller);
    const gallery = this.closest('#MainContent .product-information__media > media-gallery');
    const scroller = gallery?.querySelector('slideshow-slides');
    if (!scroller || !this.querySelector('button') || !this.querySelector('output')) return false;
    this.#startupObserver?.disconnect();
    this.#startupObserver = undefined;
    this.controller = new ProductImageZoom(gallery, scroller, this);
    return true;
  };

  connectedCallback() {
    queueMicrotask(() => {
      if (!this.isConnected || this.#initialize()) return;
      const gallery = this.closest('#MainContent .product-information__media > media-gallery');
      if (!gallery) return;
      // Async head loading can define this element before its children or the
      // following slideshow-slides have been parsed. Observe only this gallery
      // until the required nodes exist, then release the observer immediately.
      this.#startupObserver?.disconnect();
      this.#startupObserver = new MutationObserver(this.#initialize);
      this.#startupObserver.observe(gallery, { childList: true, subtree: true });
    });
  }

  disconnectedCallback() {
    this.#startupObserver?.disconnect();
    this.#startupObserver = undefined;
    this.controller?.destroy();
    this.controller = undefined;
  }
}

if (!customElements.get('product-photo-zoom')) {
  customElements.define('product-photo-zoom', ProductPhotoZoom);
}
